// DiaryDetail.js
import React, { useState, useEffect, useRef } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import axios from 'axios';
import { useParams, useLocation } from 'react-router-dom';
import { RiFileReduceLine, RiFileDownloadLine } from "react-icons/ri";
import { TbMailShare } from "react-icons/tb";
import { playMixedLanguageTTS, stopSpeech } from './utils/ttsService';
import { marked } from 'marked';
import './Clipboard.css';

const SERVER_ROOT = process.env.REACT_APP_SERVER_ROOT || 'http://localhost:3001';

// 모바일 감지
const isMobile = () => /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
const isMobileDevice = isMobile();

// modules 및 formats 변수 정의 (Clipboard.js에서 가져와야 함)
let modules;
let formats;

if (isMobileDevice) {
    modules = {
        toolbar: [
            [{ header: [1, 2, 3, false] }],
            ['bold', 'italic'],
            [{ align: ['', 'center', 'right', 'justify'] }],
            [{ indent: '-1' }, { indent: '+1' }],  
            ['link', 'image', 'video'],
            ['code-block'],
        ],
    };
    formats = ['header', 'bold', 'italic', 'align', 'indent', 'link', 'image', 'video', 'code-block'];
} else {
    modules = {
        toolbar: { 
          container: [
            [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
            ['bold', 'italic', 'underline', 'strike'],
            [{ 'color': [] }, { 'background': [] }],
            [{ 'align': [] }],
            [{ 'list': 'ordered'}, { 'list': 'bullet' }],
            [{ 'indent': '-1'}, { 'indent': '+1' }],
            [{ 'script': 'sub'}, { 'script': 'super' }],
            ['blockquote', 'code-block'],
            ['link', 'image', 'video'],
            ['clean'],
            ['latex-inline', 'latex-block'] // 수식 버튼 추가
          ],
          handlers: {
            'latex-inline': function() {
              const range = this.quill.getSelection();
              if (range) {
                const value = prompt('인라인 LaTeX 수식을 입력하세요:');
                if (value) {
                  this.quill.insertText(range.index, '$' + value + '$', 'user');
                }
              }
            },
            'latex-block': function() {
              const range = this.quill.getSelection();
              if (range) {
                const value = prompt('블록 LaTeX 수식을 입력하세요:');
                if (value) {
                  this.quill.insertText(range.index, '\n$$' + value + '$$\n', 'user');
                }
              }
            }
          },
        }, 
        clipboard: {
            matchVisual: false
        }
    };
    
    formats = [
        'header',
        'bold', 'italic', 'underline', 'strike',
        'color', 'background',
        'align',
        'list', 'bullet', 'ordered',
        'indent',
        'script', 'sub', 'super',
        'blockquote', 'code-block',
        'link', 'image', 'video'
    ];
}

// 유틸리티 함수들
const isEncoded = (uri) => {
  try {
    return uri !== decodeURIComponent(uri);
  } catch (e) {
    return false;
  }
};

// LaTeX 수식 처리 함수들
const processLatexInMarkdown = (markdown) => {
  // 인라인 수식 ($ ... $) 처리
  let processed = markdown.replace(/\$([^\$]+)\$/g, (match, latex) => {
    return `<span class="inline-latex" data-latex="${encodeURIComponent(latex)}">${latex}</span>`;
  });
  
  // 블록 수식 ($$ ... $$) 처리
  processed = processed.replace(/\$\$([^\$]+)\$\$/g, (match, latex) => {
    return `<div class="block-latex" data-latex="${encodeURIComponent(latex)}">${latex}</div>`;
  });
  
  return processed;
};

// 마크다운을 향상된 HTML로 변환하는 함수
const convertMarkdownToEnhancedHtml = (markdown) => {
  // LaTeX 수식 처리
  const processedMarkdown = processLatexInMarkdown(markdown);

  // marked 옵션 최적화
  const html = marked.parse(processedMarkdown, {
    breaks: true,
    gfm: true,
    headerIds: true,
    mangle: false,
    pedantic: false,
    sanitize: false,
    smartLists: true,
    smartypants: true,
    xhtml: true
  });
  
  // 코드 블록에 추가적인 클래스 부여
  const enhancedHtml = html
    .replace(/<pre><code>/g, '<pre class="enhanced-code-block"><code>')
    .replace(/<pre><code class="language-/g, '<pre class="enhanced-code-block"><code class="language-')
    .replace(/<code>/g, '<code class="enhanced-inline-code">');
    
  return enhancedHtml;
};

// 내용 렌더링 함수
const renderEnhancedContent = (content) => {
  if (!content) return '';
  
  // 인코딩 확인 및 디코딩
  const decodedContent = isEncoded(content) ? decodeURIComponent(content) : content;
  
  // 내용에 LaTeX 수식이 있는지 검사
  const hasLatex = /\$\$(.+?)\$\$|\$(.+?)\$/g.test(decodedContent);
  
  // 이미 HTML이고 LaTeX가 없으면 그대로 반환
  if (decodedContent.startsWith('<') && !hasLatex) {
    return decodedContent;
  }
  
  // LaTeX 수식 직접 처리 (마크다운 변환 전)
  let processedContent = decodedContent;
  
  // 블록 수식 ($$...$$) 처리
  processedContent = processedContent.replace(/\$\$([^\$]+)\$\$/g, (match, latex) => {
    try {
      // KaTeX로 HTML 바로 생성
      if (typeof window !== 'undefined' && window.katex) {
        return window.katex.renderToString(latex.trim(), {
          displayMode: true,
          throwOnError: false
        });
      }
    } catch (e) {
      console.error("Error rendering block LaTeX:", e);
    }
    // KaTeX 렌더링 실패 시 원본 반환
    return match; 
  });
  
  // 인라인 수식 ($...$) 처리
  processedContent = processedContent.replace(/\$([^\$]+)\$/g, (match, latex) => {
    try {
      // KaTeX로 HTML 바로 생성
      if (typeof window !== 'undefined' && window.katex) {
        return window.katex.renderToString(latex.trim(), {
          displayMode: false,
          throwOnError: false
        });
      }
    } catch (e) {
      console.error("Error rendering inline LaTeX:", e);
    }
    // KaTeX 렌더링 실패 시 원본 반환
    return match;
  });
  
  // 마크다운 감지 및 처리
  const mightBeMarkdown = /(\#{1,6}\s.+)|(\*\*.+\*\*)|(\*.+\*)|(\[.+\]\(.+\))|(\n\s*[\*\-\+]\s.+)|(\n\s*\d+\.\s.+)|(\n\s*\>\s.+)|(\`\`\`[\s\S]*?\`\`\`)|(\`[^\`]+\`)/.test(processedContent);
  
  if (mightBeMarkdown) {
    // 마크다운을 HTML로 변환
    return marked.parse(processedContent, {
      breaks: true,
      gfm: true,
      headerIds: true,
      mangle: false,
      pedantic: false,
      sanitize: false,
      smartLists: true,
      smartypants: true,
      xhtml: true
    });
  }
  
  // 일반 텍스트인 경우
  return `<p>${processedContent.replace(/\n/g, '<br>')}</p>`;
};

const DiaryDetail = () => {
  const { id } = useParams();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  
  // URL에서 전달받은 사용자 정보
  const user = {
    uid: queryParams.get('uid'),
    email: queryParams.get('email'),
    displayName: queryParams.get('displayName')
  };
  
  const language = queryParams.get('language') || 'EN';
  
  const [diary, setDiary] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isLocalData, setIsLocalData] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editedDiaryText, setEditedDiaryText] = useState('');
  const [editedFiles, setEditedFiles] = useState([]);
  const [editedDiaryTitle, setEditedDiaryTitle] = useState('');
  const [cursorPosition, setCursorPosition] = useState(null);
  const [showViewer, setShowViewer] = useState({});
  const [isSpeaking, setIsSpeaking] = useState(false);
  
  const quillRef = useRef(null);
  
  const containerHeightDefault = "100%";
  const quillHeightDefault = "100%";
  
  const [containerHeight, setContainerHeight] = useState(containerHeightDefault);
  const [quillHeight, setQuillHeight] = useState(quillHeightDefault);
  
  // 다이어리 세부 정보 가져오기
  useEffect(() => {
    const fetchDiaryDetails = async () => {
      try {
        setIsLoading(true);
        const response = await axios.get(`${SERVER_ROOT}/api/diary/${id}`);
        setDiary(response.data);
        setIsLocalData(false);
        
        // 로컬 스토리지에 저장
        localStorage.setItem(`diary_${id}`, JSON.stringify(response.data));
        
        setIsLoading(false);
      } catch (err) {
        console.error("Error fetching diary details:", err);
        setError(language === 'EN' ? 'This is the content from local storage.' : '로컬 저장소의 내용입니다.');
        
        // 로컬 데이터 로드
        loadLocalSelectedDiary(id);
        setIsLoading(false);
      }
    };
    
    const loadLocalSelectedDiary = (id) => {
      const localData = localStorage.getItem(`diary_${id}`);
      if (localData) {
        const diary = JSON.parse(localData);
        if (diary.id === id) {
          setDiary(diary);
          setIsLocalData(true);
        }
      }
    };
    
    fetchDiaryDetails();
  }, [id, language]);
  
  // LaTeX 수식 렌더링
  useEffect(() => {
    // LaTeX 수식 렌더링 함수
    const renderLatexElements = () => {
      if (typeof window !== 'undefined' && window.katex) {
        // 인라인 수식 렌더링
        document.querySelectorAll('.inline-latex').forEach(element => {
          try {
            const latex = decodeURIComponent(element.getAttribute('data-latex'));
            window.katex.render(latex, element, {
              throwOnError: false,
              displayMode: false
            });
          } catch (error) {
            console.error('Error rendering inline LaTeX:', error);
          }
        });
        
        // 블록 수식 렌더링
        document.querySelectorAll('.block-latex').forEach(element => {
          try {
            const latex = decodeURIComponent(element.getAttribute('data-latex'));
            window.katex.render(latex, element, {
              throwOnError: false,
              displayMode: true
            });
          } catch (error) {
            console.error('Error rendering block LaTeX:', error);
          }
        });
      }
    };
    
    // KaTeX 라이브러리 로드 확인 및 수식 렌더링
    if (diary) {
      // KaTeX가 전역으로 노출되었는지 확인
      if (typeof window !== 'undefined' && !window.katex) {
        window.katex = require('katex');
      }
      
      // 약간의 지연 후 렌더링 (DOM이 완전히 업데이트된 후)
      setTimeout(renderLatexElements, 100);
    }
  }, [diary]);
  
  // 편집 모드 설정
  const handleEditClick = (diary) => {
    setIsEditing(true);
    
    setEditedDiaryTitle(diary.titleText || '');
    setEditedDiaryText(
      isEncoded(diary.diaryText)
        ? decodeURIComponent(diary.diaryText)
        : diary.diaryText
    );
    setEditedFiles(diary.files || []);
  };
  
  // 텍스트 변경 핸들러
  const handleTextChange = (value) => {
    setEditedDiaryText(value);
    
    if (quillRef.current) {
      adjustHeight(value);
    }
  };
  
  // 에디터 변경 핸들러
  const handleEditorChange = (value, delta, source, editor) => {
    setEditedDiaryText(value);
    
    setCursorPosition(editor.getSelection());
    
    if (quillRef.current) {
      adjustHeight(value);
    }
  };
  
  // 에디터 선택 영역 변경 핸들러
  const handleSelectionChange = (range, source, editor) => {
    if (range) {
      setCursorPosition(range);
    }
  };
  
  // 고도 조정 함수
  const adjustHeight = (value) => {
    if (!quillRef.current) {
      return;
    }
    
    const lineHeight = 24;
    const padding = 20;
    const editor = quillRef.current.getEditor();
    const lineCount = editor.getLines().length;
    
    const buf = lineHeight * lineCount + padding;
    
    const newHeight = buf > quillHeightDefault ? buf : quillHeightDefault;
    const newContainerHeight = newHeight;
    
    setQuillHeight(`${newHeight}px`);
    setContainerHeight(`${newContainerHeight}px`);
  };
  
  // 파일 업로드 핸들러
  const handleFileUpload = async (e) => {
    const newFiles = Array.from(e.target.files);
    if (!newFiles.length) return;
    
    setEditedFiles(prevFiles => [...prevFiles, ...newFiles]);
    
    if (quillRef.current) {
      const quill = quillRef.current.getEditor();
      
      let insertPosition = cursorPosition ? cursorPosition.index : quill.getLength() - 1;
      if (insertPosition < 0) insertPosition = 0;
      
      for (const file of newFiles) {
        const fileName = file.name;
        const linkText = fileName;
        
        quill.insertText(insertPosition, linkText, 'link', `#edit-file-${encodeURIComponent(fileName)}`, 'user');
        quill.insertText(insertPosition + linkText.length, ' ', 'normal', null, 'user');
        
        insertPosition = insertPosition + linkText.length + 1;
        quill.setSelection(insertPosition, 0);
      }
      
      setCursorPosition({
        index: insertPosition,
        length: 0
      });
    }
  };
  
  // 저장 핸들러
  const handleSaveEdit = async (diaryId) => {
    try {
      const newFiles = editedFiles.filter(
        (editedFile) => !diary.files.some(
          (originalFile) => originalFile.filename === (editedFile.name || editedFile.filename)
        )
      );
      
      const filesToDelete = diary.files.filter(
        (originalFile) => !editedFiles.some(
          (editedFile) => (editedFile.name || editedFile.filename) === originalFile.filename
        )
      );
      
      const formData = new FormData();
      formData.append("diaryText", encodeURIComponent(editedDiaryText));
      formData.append("diaryTitle", editedDiaryTitle);
      formData.append("email", user.email);
      
      const actualNewFiles = newFiles.filter(file => file instanceof File || file.size);
      actualNewFiles.forEach((file) => {
        formData.append("files", file, encodeURIComponent(file.name || file.filename));
      });
      
      const response = await axios.put(
        `${SERVER_ROOT}/api/diaryFileAdd`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
          params: { 
            id: diaryId,
            email: user.email
          }
        }
      );
      
      const updatedDiaryResponse = await axios.get(`${SERVER_ROOT}/api/diary/${diaryId}`);
      const updatedFiles = updatedDiaryResponse.data.files || [];
      
      let updatedText = editedDiaryText;
      let hasChanges = false;
      
      for (const file of actualNewFiles) {
        const fileName = file.name || file.filename;
        
        const editPattern = new RegExp(`href="#edit-file-${encodeURIComponent(fileName).replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}"`, 'g');
        const standardPattern = new RegExp(`href="#file-${encodeURIComponent(fileName).replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}"`, 'g');
        
        const uploadedFile = updatedFiles.find(f => {
          return (
            f.filename.includes(encodeURIComponent(fileName)) || 
            decodeURIComponent(f.filename).includes(fileName) ||
            f.originalname === encodeURIComponent(fileName) ||
            f.originalname === fileName
          );
        });
        
        if (uploadedFile) {
          const fileUrl = `${SERVER_ROOT}/uploads/${uploadedFile.filename}`;
          
          let newText = updatedText.replace(editPattern, `href="${fileUrl}" target="_blank" rel="noopener noreferrer"`);
          newText = newText.replace(standardPattern, `href="${fileUrl}" target="_blank" rel="noopener noreferrer"`);
          
          if (newText !== updatedText) {
            updatedText = newText;
            hasChanges = true;
          }
        }
      }
      
      if (hasChanges) {
        await axios.put(`${SERVER_ROOT}/api/diary/${diaryId}/update-links`, {
          diaryText: encodeURIComponent(updatedText),
          email: user.email
        });
      }
      
      alert(language === "KR" ? "수정이 완료되었습니다." : "Diary updated successfully.");
      setIsEditing(false);
      
      // 다이어리 다시 로드
      const refreshResponse = await axios.get(`${SERVER_ROOT}/api/diary/${id}`);
      setDiary(refreshResponse.data);
      
    } catch (error) {
      console.error("Error saving diary:", error);
      alert(language === "KR" ? "수정을 저장하는 중 문제가 발생했습니다." : "Failed to save diary.");
    }
  };
  
  // 취소 핸들러
  const handleCancelEdit = () => {
    setIsEditing(false);
  };
  
  // 삭제 핸들러
  const handleDelete = async (id) => {
    if (!window.confirm(language === 'EN' ? 'Are you sure you want to delete this record?' : '정말 삭제하시겠습니까?')) {
      return;
    }
    
    try {
      const response = await axios.delete(`${SERVER_ROOT}/api/diary/${id}`);
      if (response.status === 200) {
        alert(language === 'EN' ? 'Record deleted successfully.' : '기록이 성공적으로 삭제되었습니다.');
        window.close();
      } else {
        alert(language === 'EN' ? 'Failed to delete the record.' : '기록 삭제에 실패했습니다.');
      }
    } catch (error) {
      console.error('Error deleting record:', error);
      alert(language === 'EN' ? 'An error occurred while deleting the record.' : '기록을 삭제하는 중 오류가 발생했습니다.');
    }
  };
  
  // 공유 핸들러
  const handleShareDiaryClick = (diaryId) => {
    const recipientEmail = prompt("Enter recipient's email address:");
    if (!recipientEmail) {
      alert("Email address is required!");
      return;
    }
    
    fetch(`${SERVER_ROOT}/api/share-diary`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        diaryId,
        recipientEmail,
      }),
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.success) {
          alert("Diary has been successfully shared!");
        } else {
          alert(`Failed to share diary: ${data.error}`);
        }
      })
      .catch((error) => {
        console.error("Error sharing diary:", error);
        alert("An error occurred while sharing the diary.");
      });
  };
  
  // 파일 다운로드 핸들러
  const handleFileDownload = async (filename) => {
    try {
      const fileUrl = `${SERVER_ROOT}/uploads/${filename}`;
      const response = await fetch(fileUrl);
      
      if (!response.ok) {
        throw new Error('Network response was not ok');
      }
      
      const blob = await response.blob();
      
      const downloadLink = document.createElement('a');
      downloadLink.href = URL.createObjectURL(blob);
      downloadLink.download = filename;
      downloadLink.style.display = 'none';
      
      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);
    } catch (error) {
      console.error('File download failed:', error);
      alert('Failed to download the file.');
    }
  };
  
  // 터치 핸들러 함수
  const handlePinchZoom = (event) => {
    if (event.touches.length === 2) {
      const content = event.currentTarget;
      
      const touch1 = event.touches[0];
      const touch2 = event.touches[1];
      
      const distance = Math.sqrt(
        Math.pow(touch2.pageX - touch1.pageX, 2) +
        Math.pow(touch2.pageY - touch1.pageY, 2)
      );
      
      if (!content.lastDistance) {
        content.lastDistance = distance;
        
        const rect = content.getBoundingClientRect();
        const centerX = (touch1.pageX + touch2.pageX) / 2 - rect.left;
        const centerY = (touch1.pageY + touch2.pageY) / 2 - rect.top;
        
        content.style.transformOrigin = `${centerX}px ${centerY}px`;
      }
      
      const scale = distance / content.lastDistance;
      const currentScale = content.scale || 1;
      const newScale = Math.min(Math.max(currentScale * scale, 1), 4);
      
      content.style.transform = `translate(${content.translateX || 0}px, ${content.translateY || 0}px) scale(${newScale})`;
      content.scale = newScale;
      content.lastDistance = distance;
    }
  };
  
  const handleTouchEnd = (event) => {
    const content = event.currentTarget;
    delete content.lastDistance;
  };
  
  const handleDragStart = (event) => {
    if (event.touches.length === 1) {
      const content = event.currentTarget;
      content.dragging = true;
      
      const touch = event.touches[0];
      content.startX = touch.pageX - (content.translateX || 0);
      content.startY = touch.pageY - (content.translateY || 0);
    }
};

const handleDragMove = (event) => {
  if (event.touches.length === 1) {
    const content = event.currentTarget;
    if (content.dragging) {
      const touch = event.touches[0];
      const translateX = touch.pageX - content.startX;
      const translateY = touch.pageY - content.startY;
      
      content.style.transform = `translate(${translateX}px, ${translateY}px) scale(${content.scale || 1})`;
      content.translateX = translateX;
      content.translateY = translateY;
    }
  }
};

const handleDragEnd = (event) => {
  const content = event.currentTarget;
  content.dragging = false;
};

// 로딩 중
if (isLoading) {
  return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
      {language === 'KR' ? '로딩 중...' : 'Loading...'}
    </div>
  );
}

// 오류 발생
if (error && !diary) {
  return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', color: 'red' }}>
      {error}
    </div>
  );
}

// 다이어리 없음
if (!diary) {
  return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
      {language === 'KR' ? '다이어리를 찾을 수 없습니다.' : 'Diary not found.'}
    </div>
  );
}

return (
  <div className="diary-details">
    {isLocalData && (
      <span style={{ color: 'red', fontWeight: 'bold' }}>
        {language === 'EN' ? 'This is the content from local storage.' : '로컬 저장소의 내용입니다.'}
      </span>
    )}
    
    {isEditing ? (
      <div className="DiaryForm">
        <div className="title-input">
          <label> {'Title: '}
            <input
              type="text"
              value={editedDiaryTitle}
              onChange={(e) => setEditedDiaryTitle(e.target.value)}
              placeholder={language === 'KR' ? '제목을 입력하세요' : 'Enter a title'}
              className="diary-title-input"
            />
          </label>
        </div>
        
        <div className="textArea"
          style={{
            height: containerHeight,
            overflow: "visible",
            transition: "height 0.3s ease",
          }}
        >
          <ReactQuill
            ref={quillRef}
            value={editedDiaryText}
            onChange={handleEditorChange}
            onChangeSelection={handleSelectionChange}
            modules={modules}
            formats={formats}
            theme="snow"
            style={{
              width: "98%",
              height: quillHeight,
              marginLeft: "1%",
              fontSize: "10px",
            }}
          />
        </div>
        
        <div>
          <label>
            {language === 'KR' ? '첨부 파일 추가:' : 'Add Attachments:'}
            <input
              type="file"
              multiple
              onChange={handleFileUpload}
              onClick={() => {
                if (!cursorPosition && quillRef.current) {
                  const quill = quillRef.current.getEditor();
                  const range = quill.getSelection();
                  if (range) {
                    setCursorPosition(range);
                  }
                }
              }}
            />
          </label>
          {editedFiles.length > 0 && (
            <div className="file-list-dropdown">
              <div 
                className="file-list-dropdown-header"
                onClick={() => {
                  const content = document.getElementById('edit-mode-file-list');
                  if (content) content.classList.toggle('show');
                }}
              >
                <span>{language === 'KR' ? `첨부 파일 (${editedFiles.length})` : `Attached Files (${editedFiles.length})`}</span>
                <span>▼</span>
              </div>
              <div id="edit-mode-file-list" className="file-list-dropdown-content">
                {editedFiles.map((file, index) => (
                  <div key={index} className="file-list-item">
                    <span>{((file.name || file.filename || '').replace(/^[^_]+_[^_]+_/, ''))}</span>
                    <button
                      onClick={async () => {
                        const fileName = file.name || file.filename;
                        try {
                          const response = await axios.delete(`${SERVER_ROOT}/api/diary-delete-file`, {
                            params: { 
                              id: diary.id,  
                              email: user.email, 
                              filename: fileName,
                            } 
                          });
                          
                          if (response.status === 200) {
                            console.log(`${fileName} deleted successfully.`);
                            // 클라이언트 상태에서도 파일 제거
                            setEditedFiles((prev) => prev.filter((_, i) => i !== index));
                          } else {
                            console.error(`Failed to delete file: ${fileName}`);
                          }
                        } catch (error) {
                          console.error('Error deleting file:', error);
                          alert(language === 'KR' ? '파일 삭제 중 문제가 발생했습니다.' : 'An error occurred while deleting the file.');
                        }
                      }}
                    >
                      {language === 'KR' ? '삭제' : 'Remove'}
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        
        <div>
          <button onClick={() => handleSaveEdit(diary.id)}>
            {language === 'KR' ? '저장' : 'Save'}
          </button>
          <button onClick={handleCancelEdit}>
            {language === 'KR' ? '취소' : 'Cancel'}
          </button>
        </div>
      </div>
    ) : (
      <>
        <div className='memo-topinfo' >
          <div style={{width: '200px'}}>
            {new Date(diary.selectedDate).toLocaleDateString(
              language !== 'KR' ? 'en-US' : 'ko-KR'
            )}
            {' '}
            {new Date(diary.currentDateTime).toLocaleTimeString(
              language !== 'KR' ? 'en-US' : 'ko-KR',
              { hour: '2-digit', minute: '2-digit' }
            )}
          </div>
          
          {/* TTS 버튼 추가 */}
          <button
            className="tts-button"
            onClick={() => {
              if (isSpeaking) {
                stopSpeech(setIsSpeaking);
              } else {  
                const titleText = isEncoded(diary.titleText) 
                  ? decodeURIComponent(diary.titleText) 
                  : diary.titleText; 
                  
                const diaryText = isEncoded(diary.diaryText) 
                  ? decodeURIComponent(diary.diaryText) 
                  : diary.diaryText;
                  
                playMixedLanguageTTS(titleText+'!,'+diaryText, setIsSpeaking);
              }
            }}
            style={{ 
              marginRight: '5%', 
              background: isSpeaking ? '#e0f7fa' : 'transparent', 
              color: isSpeaking ? '#00796b' : 'gray',
              border: isSpeaking ? '1px solid #80cbc4' : 'none',
              borderRadius: '4px',
              padding: '5px 10px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '12px'
            }}
          >
            {isSpeaking ? 
              (language === 'KR' ? '읽기 중지' : 'Stop Reading') : 
              (language === 'KR' ? '텍스트 읽기' : 'Read Text')}
          </button>
          
          <button
            className="share-diary-etails-icon"
            onClick={() => handleShareDiaryClick(diary.id)}
            style={{ marginRight: '5%', background: 'transparent', color: 'gray'}}
          > 
            <TbMailShare style={{width: '20px', height: 'auto'}}/>
          </button>
          
          <button onClick={() => handleEditClick(diary)} style={{width: '60px'}}>
            {language === 'KR' ? '편집' : 'Edit'}
          </button>
          
          <button
            className="close-details-icon"
            onClick={() => window.close()}
            style={{ marginRight: '5%', background: 'transparent', color: 'gray'}}
          >
            <RiFileReduceLine style={{width: '20px', height: 'auto'}}/>
          </button>
          
          {/* Delete Button */}
          <button 
            className="delete-button" 
            onClick={() => handleDelete(diary.id)}
            style={{color: 'gray', backgroundColor: 'transparent'}}
          >
            {language === 'EN' ? 'Delete' : '삭제'}
          </button>
        </div>
        
        {/* 메모 내용 표시 */}
        <div className="diary-text enhanced-diary-content" 
          style={{
            width: "98%",
            marginLeft: "1%",
            padding: "12px",
            backgroundColor: "transparent", 
            minHeight: "200px",
            height: "85%",
            overflow: "auto"
          }}
        >
          <div 
            dangerouslySetInnerHTML={{ 
              __html: renderEnhancedContent(
                isEncoded(diary.diaryText) 
                  ? decodeURIComponent(diary.diaryText) 
                  : diary.diaryText
              )
            }}
            className="ql-editor"
            style={{
              height: "89%",
              width: "98%",
              marginLeft: "1%"
            }}
          ></div>
        </div>
        
        {diary.voiceFileName && (
          <>
            <audio controls>
              <source
                src={`${SERVER_ROOT}/uploads/${diary.voiceFileName}`}
                type="audio/mpeg"
              />
              {language === 'EN' ? 'Your browser does not support audio playback.' : '브라우저가 음성 재생을 지원하지 않습니다.'}
            </audio>
            <button onClick={() => handleFileDownload(diary.voiceFileName)} style={{ cursor: 'pointer', color: 'blue', border: 'none', background: 'none' }}>
              {language === 'EN' ? 'Download' : '다운로드'}
            </button>
          </>
        )}
        
        {diary.photoFileName && (
          <div className="diary-photo">
            <img
              src={`${SERVER_ROOT}/uploads/${diary.photoFileName}`}
              alt={language === 'EN' ? 'Diary Photo' : '일기 사진'}
            />
          </div>
        )}
        
        {diary.files && diary.files.length > 0 && (
          <div className='attachedFiles'>
            <div className="file-list-dropdown">
              <div 
                className="file-list-dropdown-header"
                onClick={() => {
                  const content = document.getElementById('diary-view-file-list');
                  if (content) content.classList.toggle('show');
                }}
              >
                <span>{language === 'KR' ? `첨부 파일 (${diary.files.length})` : `Attached Files (${diary.files.length})`}</span>
                <span>▼</span>
              </div>
              <div id="diary-view-file-list" className="file-list-dropdown-content">
                {diary.files.map((file, index) => {
                  const fileUrl = `${SERVER_ROOT}/uploads/${file.filename}`;
                  const fileExtension = (file.filename || file.name).split('.').pop().toLowerCase();
                  
                  return (
                    <div key={index} className="file-list-item">
                      <span
                        style={{
                          cursor: 'pointer',
                          color: 'gray',
                          textDecoration: 'underline',
                        }}
                        onClick={() => setShowViewer((prevState) => ({ ...prevState, [index]: !prevState[index] }))}
                      >
                        {(file.filename || file.name).replace(/^[^_]+_[^_]+_/, '')}
                      </span>
                      <button
                        onClick={() => handleFileDownload(file.filename)}
                        style={{ 
                          cursor: 'pointer', 
                          color: 'gray', 
                          border: 'none', 
                          fontSize: '12px',
                          background: 'none' 
                        }}
                      >
                        {<RiFileDownloadLine />}
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
            
            {/* 파일 뷰어 부분 */}
            {diary.files.map((file, index) => {
              const fileUrl = `${SERVER_ROOT}/uploads/${file.filename}`;
              const fileExtension = (file.filename || file.name).split('.').pop().toLowerCase();
              
              return (
                <div key={`viewer-${index}`}>
                  {showViewer[index] && (
                    <div className="viewer-container" style={{marginTop: '10px'}}>
                      {['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(fileExtension) && (
                        <img
                          src={fileUrl}
                          alt={file.filename}
                          className="zoomable"
                          onTouchStart={(event) => {
                            handleDragStart(event);
                            handlePinchZoom(event);
                          }}
                          onTouchMove={(event) => {
                            handleDragMove(event);
                            handlePinchZoom(event);
                          }}
                          onTouchEnd={(event) => {
                            handleDragEnd(event);
                            handleTouchEnd(event);
                          }}
                          style={{ maxWidth: '100%', height: 'auto', margin: '10px 0' }}
                        />
                      )}
                      {['mp3', 'wav', 'ogg', 'm4a'].includes(fileExtension) && (
                        <audio controls style={{ width: '100%', margin: '10px 0' }}>
                          <source 
                            src={fileUrl} 
                            type={fileExtension === 'm4a' ? 'audio/mp4' : `audio/${fileExtension}`} 
                          />
                          {language === 'KR'
                            ? '브라우저가 오디오 재생을 지원하지 않습니다.'
                            : 'Your browser does not support audio playback.'}
                        </audio>
                      )}
                      {['mp4', 'webm', 'ogg', 'mkv', 'mov'].includes(fileExtension) && (
                        <div style={{ width: '100%', display: 'flex', justifyContent: 'center', margin: '10px 0' }}>
                          <video 
                            controls 
                            className="zoomable"
                            onTouchStart={(event) => {
                              handleDragStart(event);
                              handlePinchZoom(event);
                            }}
                            onTouchMove={(event) => {
                              handleDragMove(event);
                              handlePinchZoom(event);
                            }}
                            onTouchEnd={(event) => {
                              handleDragEnd(event);
                              handleTouchEnd(event);
                            }}
                            style={{ width: '60%', margin: '10px 0' }}
                          >
                            <source src={fileUrl} type={`video/${fileExtension}`} />
                            {language === 'KR'
                              ? '브라우저가 비디오 재생을 지원하지 않습니다.'
                              : 'Your browser does not support video playback.'}
                          </video>
                        </div>
                      )}
                      {['pdf'].includes(fileExtension) && (
                        <iframe
                          src={fileUrl}
                          className="zoomable"
                          onTouchStart={(event) => {
                            handleDragStart(event);
                            handlePinchZoom(event);
                          }}
                          onTouchMove={(event) => {
                            handleDragMove(event);
                            handlePinchZoom(event);
                          }}
                          onTouchEnd={(event) => {
                            handleDragEnd(event);
                            handleTouchEnd(event);
                          }}
                          style={{ width: '100%', height: '700px', margin: '10px 0' }}
                          frameBorder="0"
                          title={file.filename}
                        ></iframe>
                      )}
                      {!['jpg', 'jpeg', 'png', 'gif', 'webp', 'mp3', 'wav', 'ogg', 'm4a', 'mp4', 'webm', 'ogg', 'pdf', 'mkv', 'mov'].includes(
                        fileExtension
                      ) && (
                        <a href={fileUrl} target="_blank" rel="noopener noreferrer">
                          {file.filename.replace(/^[^_]+_[^_]+_/, '')}
                        </a>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
        
        <div className='bottom-buttons' style={{ display: 'flex', alignItems: 'center', gap: '10px'}}>
          <button
            className="close-details-button"
            onClick={() => window.close()}
          >
            {language === 'EN' ? 'Close' : '닫기'}
          </button>
          
          <button 
            className="delete-button" 
            onClick={() => handleDelete(diary.id)}
            style={{color: 'gray', backgroundColor: 'transparent'}}
          >
            {language === 'EN' ? 'Delete' : '삭제'}
          </button>
        </div>
      </>
    )}
  </div>
);
};

export default DiaryDetail;